import { SkillEditorData } from '@shared/components/extensions/editors/skillEditorData';
import { AgentSkillModel } from '@shared/models/agentExtensions/AgentSkillModel';
import {
    CreateSkillInput,
    DEFAULT_SKILL_ACTIVATION,
    SkillModel,
    UpdateSkillInput,
} from '@shared/models/skills/SkillModel';
import { buildSkillPackage, parseSkillPackageBody } from '@shared/services/skills/skillPackage';
import { createSkill, getSkillPackageContent, updateSkill, uploadSkillPackage } from '@shared/api/skills/skillsApi';

/**
 * REST-backed {@link SkillEditorData} for the admin portal. Org admins pre-configure org-scoped skills
 * from the same editor the desktop app uses. The portal has no local filesystem, so native file
 * management is disabled (`manageFiles: false`) and the SKILL.md body is packaged into a zip in the
 * browser and uploaded to the API.
 */
export function createRestSkillEditorData(organizationId: string): SkillEditorData {
    return {
        capabilities: { manageFiles: false },

        async getBody(skillId) {
            // Download the package through the API (CORS-allowed), not the private-blob SAS URI,
            // which the browser blocks. Returns the empty body for a metadata-only skill.
            const result = await getSkillPackageContent(skillId);
            if (result.resultType !== 'Ok' || !result.data) return '';
            return parseSkillPackageBody(await result.data.arrayBuffer());
        },

        async createSkill(input: CreateSkillInput): Promise<SkillModel> {
            const result = await createSkill({
                name: input.name,
                displayName: titleize(input.name),
                description: input.description,
                categories: input.keywords ?? [],
                version: '1',
                organizationId,
            });
            if (result.resultType !== 'Ok' || !result.data) {
                throw new Error(errorMessage(result, 'Could not create the skill.'));
            }
            await uploadPackage(result.data.id, input);
            return toLocalSkill(result.data);
        },

        async updateSkill(input: UpdateSkillInput): Promise<SkillModel> {
            const result = await updateSkill(input.id, {
                name: input.name ?? '',
                displayName: titleize(input.name ?? ''),
                description: input.description,
                categories: input.keywords ?? [],
            });
            if (result.resultType !== 'Ok' || !result.data) {
                throw new Error(errorMessage(result, 'Could not save the skill.'));
            }
            // The editor always sends the current body on save; repackage and re-upload it.
            await uploadPackage(input.id, input);
            return toLocalSkill(result.data);
        },
    };
}

async function uploadPackage(skillId: string, input: CreateSkillInput | UpdateSkillInput): Promise<void> {
    const file = await buildSkillPackage(
        {
            name: input.name ?? '',
            description: input.description ?? '',
            keywords: input.keywords,
            activation: input.activation,
        },
        input.body
    );
    const upload = await uploadSkillPackage(skillId, file);
    if (upload.resultType !== 'Ok') {
        throw new Error(errorMessage(upload, 'Could not upload the skill package.'));
    }
}

/** Build a minimal local SkillModel from the API read model (the editor only reads `id`). */
function toLocalSkill(model: AgentSkillModel): SkillModel {
    return {
        id: model.id,
        name: model.name,
        description: model.description ?? '',
        keywords: [],
        dirPath: '',
        skillMdPath: '',
        source: 'organization',
        enabled: false,
        activation: DEFAULT_SKILL_ACTIVATION,
        hasScripts: false,
        hasReferences: false,
        visibility: 'organization',
        createdAt: Date.parse(model.createdDate) || 0,
        updatedAt: Date.parse(model.modifiedDate) || 0,
        remoteId: model.id,
        organizationId: model.organizationId,
        isOfficial: model.isOfficial,
    };
}

function titleize(name: string): string {
    return name
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function errorMessage(result: { errors?: string[] }, fallback: string): string {
    return result.errors?.[0] ?? fallback;
}
